/**
 * Resolve the canonical membership tied to an authenticated user.
 *
 * In SRAC's membership model an auth account can be associated with one or
 * two memberships:
 *
 *   - A solo member: one membership with `linkedUserId == uid`, no
 *     `primaryMembershipId`, no `secondaryMembershipId`.
 *   - A primary with a dependent: the primary's membership has
 *     `linkedUserId == uid` and `secondaryMembershipId` pointing at the
 *     dependent's membership doc. The dependent's membership has
 *     `primaryMembershipId` pointing back at the primary, and may ALSO
 *     have `linkedUserId == uid` (most common — primary manages both)
 *     or its own auth uid (when the dependent has their own login).
 *
 * The cached pointer at `users/{uid}.membershipId` is supposed to point at
 * the user's own membership, but it has historically drifted — primaries
 * occasionally end up cached at the dependent's membership, which then
 * leaks the dependent's profile into every place that asks "who am I?".
 *
 * This helper does the canonical reverse lookup: query memberships by
 * `linkedUserId`, then — when there's more than one match (the
 * primary-also-manages-dependent case) — prefer the membership that does
 * not itself carry a `primaryMembershipId`. That's the primary; the one
 * with `primaryMembershipId` set is the dependent.
 *
 * If the dependent has their own auth uid (their login is independent of
 * the primary's), the query for that uid returns ONLY the dependent's
 * membership and the helper returns it as-is — dependents who log in
 * still see their own identity.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

import {
  collection,
  query,
  where,
  limit,
  getDocs,
  type Firestore,
} from 'firebase/firestore';
import type { Membership } from '@/models/membership';

/**
 * Resolve the membership that should represent the given auth user across
 * the app. Prefers a primary over a dependent when both are linked to the
 * same uid.
 *
 * @param firestore - Client Firestore instance.
 * @param uid       - Authenticated user's Firebase Auth UID.
 * @returns The resolved membership, or `null` if no membership is linked.
 */
export async function resolveUserMembership(
  firestore: Firestore,
  uid: string,
): Promise<Membership | null> {
  const q = query(
    collection(firestore, 'memberships'),
    where('linkedUserId', '==', uid),
    limit(5),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  const candidates = snap.docs.map(
    (d) => ({ id: d.id, ...d.data() } as Membership),
  );

  // Prefer the membership that is NOT itself a dependent. If two memberships
  // share the same linkedUserId, that's the primary-managed-dependent case —
  // the dependent will have `primaryMembershipId` set; the primary won't.
  const primary = candidates.find((m) => !m.primaryMembershipId);
  return primary ?? candidates[0];
}
